import { makeStyles } from "@fluentui/react-components";
import { projectIqTokens } from "apps/project-iq/theme/projectIqTokens";
import { designTokens } from "theme/customTheme";
import {
  AMBER_INK,
  AMBER_SOFT_BG,
  GREEN_INK,
  GREEN_SOFT_BG,
} from "apps/project-iq/utils/diagnosticUtils";

const SKELETON_BREADCRUMB_WIDTH = "120px";
const SKELETON_HERO_NAME_WIDTH = "260px";
const SKELETON_HERO_SUBLINE_WIDTH = "180px";
const SKELETON_SMALL_WIDTH = "60px";
const SKELETON_TAB_PRIMARY_WIDTH = "80px";
const SKELETON_TAB_SECONDARY_WIDTH = "72px";
const SKELETON_CONTENT_HEIGHT = "200px";

export const useAnalyticsDetailPageStyles = makeStyles({
  root: {
    height: "100%",
    overflowY: "auto",
  },

  breadcrumbBar: {
    position: "sticky",
    top: 0,
    zIndex: 5,
    height: "64px",
    borderBottom: `1px solid ${projectIqTokens.line}`,
    backgroundColor: projectIqTokens.card,
    display: "flex",
    alignItems: "center",
    padding: "0 28px",
    gap: "12px",
  },

  backButton: {
    display: "flex",
    alignItems: "center",
    gap: "7px",
    border: `1px solid ${projectIqTokens.line}`,
    backgroundColor: projectIqTokens.card,
    color: projectIqTokens.muted,
    height: "36px",
    padding: "0 13px",
    borderRadius: "9px",
    fontSize: "13.5px",
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: projectIqTokens.fontFamily,
    ":focus-visible": {
      outline: `2px solid ${projectIqTokens.teal}`,
      outlineOffset: "2px",
    },
  },

  breadcrumbSeparator: {
    color: projectIqTokens.faint,
  },

  breadcrumbName: {
    fontSize: "14.5px",
    fontWeight: 700,
    color: projectIqTokens.ink,
  },

  breadcrumbRight: {
    marginLeft: "auto",
  },

  contentArea: {
    maxWidth: "920px",
    margin: "0 auto",
    padding: "32px 28px 90px",
  },

  hero: {
    "@keyframes analyticsIn": {
      from: { opacity: 0, transform: "translateY(8px)" },
      to: { opacity: 1, transform: "translateY(0)" },
    },
    animationName: "analyticsIn",
    animationDuration: "0.4s",
    animationTimingFunction: "ease",
    animationFillMode: "both",
    "@media (prefers-reduced-motion: reduce)": {
      animationDuration: "0.01ms",
    },
    marginBottom: "22px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "16px",
  },

  heroLeft: {
    display: "flex",
    alignItems: "center",
    gap: "13px",
    minWidth: 0,
    flex: 1,
  },

  heroIconTile: {
    width: "44px",
    height: "44px",
    borderRadius: designTokens.borderRadius.lg,
    flexShrink: 0,
    background: "rgba(107, 106, 217, 0.12)",
    color: projectIqTokens.violet,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "22px",
  },

  heroBody: {
    flex: 1,
    minWidth: 0,
  },

  heroName: {
    fontSize: "26px",
    fontWeight: 800,
    margin: 0,
    letterSpacing: "-0.02em",
    color: projectIqTokens.ink,
  },

  statusPill: {
    display: "inline-flex",
    alignItems: "center",
    fontSize: designTokens.fontSize.xxs,
    fontWeight: 800,
    letterSpacing: designTokens.letterSpacing.wide,
    padding: `${designTokens.spacing[1]} 10px`,
    borderRadius: "6px",
    textTransform: "uppercase",
  },

  statusPillNotScored: {
    backgroundColor: projectIqTokens.bg2,
    color: projectIqTokens.muted,
  },

  statusPillInProgress: {
    backgroundColor: AMBER_SOFT_BG,
    color: AMBER_INK,
  },

  statusPillScored: {
    backgroundColor: GREEN_SOFT_BG,
    color: GREEN_INK,
  },

  statusPillError: {
    backgroundColor: projectIqTokens.rustBg,
    color: projectIqTokens.rust,
  },

  statusPillCompleted: {
    backgroundColor: GREEN_SOFT_BG,
    color: GREEN_INK,
  },

  statusPillDraft: {
    backgroundColor: projectIqTokens.bg2,
    color: projectIqTokens.muted,
  },

  heroSubLine: {
    fontSize: "13px",
    color: projectIqTokens.muted,
    marginTop: designTokens.spacing[1],
  },

  heroSubLineAccent: {
    color: projectIqTokens.violet,
    fontWeight: 600,
  },

  tabBar: {
    display: "flex",
    gap: designTokens.spacing[1],
    borderBottom: `1px solid ${projectIqTokens.line}`,
    marginBottom: designTokens.spacing[6],
  },

  tab: {
    background: "transparent",
    border: "none",
    borderBottom: "2px solid transparent",
    padding: `10px 16px`,
    cursor: "pointer",
    fontSize: "13.5px",
    fontWeight: 700,
    color: projectIqTokens.muted,
    marginBottom: "-1px",
    transition: designTokens.transition.fast,
    fontFamily: projectIqTokens.fontFamily,
    ":focus-visible": {
      outline: `2px solid ${projectIqTokens.teal}`,
      outlineOffset: "2px",
    },
  },

  tabActive: {
    borderBottom: `2px solid ${projectIqTokens.teal}`,
    color: projectIqTokens.ink,
  },

  skeletonRow: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },

  skeletonCol: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    flex: 1,
    minWidth: 0,
  },

  skeletonBreadcrumb: {
    width: SKELETON_BREADCRUMB_WIDTH,
  },

  skeletonHeroName: {
    width: SKELETON_HERO_NAME_WIDTH,
  },

  skeletonStatusPill: {
    width: SKELETON_SMALL_WIDTH,
  },

  skeletonHeroSubline: {
    width: SKELETON_HERO_SUBLINE_WIDTH,
  },

  skeletonTabPrimary: {
    width: SKELETON_TAB_PRIMARY_WIDTH,
  },

  skeletonTabSecondary: {
    width: SKELETON_TAB_SECONDARY_WIDTH,
  },

  skeletonTabTertiary: {
    width: SKELETON_SMALL_WIDTH,
  },

  skeletonContent: {
    height: SKELETON_CONTENT_HEIGHT,
    width: "100%",
  },
});
